import '../styles/v2/pages/onwatch.css';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import fetchWithTenant from '../utils/fetchWithTenant';
import InfoTooltip from '../components/InfoTooltip';
import {
  buildMountList, gpuUnavailableInfo, filterHardwareFields, buildWarrantyLink, deriveHostTierStatus,
} from '../utils/onwatchHostDisplay.cjs';
import {
  ArrowLeft, RefreshCw, Play, Settings, Search, Loader2,
  AlertTriangle, AlertCircle, CheckCircle2, HelpCircle, ChevronRight,
  Server, Cpu, HardDrive, Layers, FileArchive, X, Download, ChevronLeft,
  Video, ShieldCheck, Zap, ChevronDown, Bell, Check, Moon, Copy,
} from 'lucide-react';

const POLL_INTERVAL = 15000;

const CATEGORY_ORDER = ['CORE', 'INFRASTRUCTURE', 'INGRESS', 'BATCH', 'MONITORING', 'UNCLASSIFIED'];
const CATEGORY_LABEL = {
  CORE: 'Core', INFRASTRUCTURE: 'Infrastructure', INGRESS: 'Ingress',
  BATCH: 'Batch', MONITORING: 'Monitoring', UNCLASSIFIED: 'Unclassified',
};
const HEALTH_RANK = { CRITICAL: 4, WARNING: 3, UNKNOWN: 2, INFORMATIONAL: 1, HEALTHY: 0 };
const HEALTH_CLASS = { HEALTHY: 'healthy', WARNING: 'warning', CRITICAL: 'critical', INFORMATIONAL: 'informational', UNKNOWN: 'unknown' };

function timeAgo(dateStr) {
  if (!dateStr) return 'never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function formatBytes(bytes) {
  if (bytes == null) return null;
  const gb = bytes / (1024 ** 3);
  if (gb >= 1) return `${gb.toFixed(1)} GB`;
  const mb = bytes / (1024 ** 2);
  return `${mb.toFixed(0)} MB`;
}

function barClass(percent) {
  if (percent == null) return 'ok';
  if (percent >= 90) return 'crit';
  if (percent >= 75) return 'warn';
  return 'ok';
}

function HealthDot({ health, large }) {
  const cls = HEALTH_CLASS[health] || 'unknown';
  return <span className={`ow-dot ${cls}${large ? ' large' : ''}`} title={health} />;
}

/* ── C1. Collection state banner ──
   UNKNOWN is the most important state on this page: everything below is
   history, not current state, and must be visually marked as stale. */
function CollectionStateBanner({ instance }) {
  const state = instance.collectionState;

  if (state === 'OK') {
    return (
      <div className="ow-state-banner ok">
        <CheckCircle2 size={18} className="ow-state-banner-icon" />
        <div className="ow-state-banner-body">
          <span className="ow-state-banner-title">
            Collection OK
            <InfoTooltip text="Whether we currently have live data from this appliance. UNKNOWN means we cannot see it, so everything below is history rather than current state." />
          </span>
          <span className="ow-state-banner-detail"> · Last updated {timeAgo(instance.lastSuccessAt)}</span>
        </div>
      </div>
    );
  }

  if (state === 'DEGRADED') {
    return (
      <div className="ow-state-banner degraded">
        <AlertTriangle size={18} className="ow-state-banner-icon" />
        <div className="ow-state-banner-body">
          <div className="ow-state-banner-title">Collection degraded</div>
          <div className="ow-state-banner-detail">{instance.lastError || 'Some queries failed this cycle.'} · Last success {timeAgo(instance.lastSuccessAt)}</div>
        </div>
      </div>
    );
  }

  if (state === 'ERROR') {
    return (
      <div className="ow-state-banner error">
        <AlertCircle size={18} className="ow-state-banner-icon" />
        <div className="ow-state-banner-body">
          <div className="ow-state-banner-title">Collection error, polling suspended</div>
          <div className="ow-state-banner-detail">
            {instance.lastError || 'Check the instance configuration.'} · <Link to={`/monitoring/onwatch`}>Go to settings</Link>
          </div>
        </div>
      </div>
    );
  }

  // UNKNOWN, including the case where a poll has never succeeded.
  return (
    <div className="ow-state-banner unknown">
      <HelpCircle size={22} className="ow-state-banner-icon" />
      <div className="ow-state-banner-body">
        <div className="ow-state-banner-title">
          Monitoring data is stale
          <InfoTooltip text="Whether we currently have live data from this appliance. UNKNOWN means we cannot see it, so everything below is history rather than current state." />
        </div>
        <div className="ow-state-banner-detail">
          {instance.lastSuccessAt
            ? `Last successful update ${timeAgo(instance.lastSuccessAt)}. Everything below reflects history, not current state.`
            : 'No successful update has ever completed. There is no current state to show.'}
        </div>
      </div>
    </div>
  );
}

const ALERT_SEVERITY_CLASS = { CRITICAL: 'critical', WARNING: 'warning', INFO: 'informational' };

function alertAge(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

/**
 * Task F: open alerts with severity, age, acknowledge and resolve. Reuses
 * the existing generic acknowledge/resolve endpoints
 * (POST /api/md/alerts/:id/acknowledge, /resolve) rather than building
 * parallel OnWatch-specific ones - they already match on {id, tenantId}
 * only, no device-type assumption.
 *
 * A composite alert (metadata.signatureId present) renders its members
 * and the remediation path, explicitly labelled as something an operator
 * runs, never something this page ran - REMEDIATION IS SURFACED, NEVER
 * EXECUTED, a permanent scope decision, not a UI limitation.
 *
 * When a tier is stale, this says so plainly rather than implying
 * everything is fine: alerting for that tier is genuinely paused, not
 * merely quiet.
 */
function AlertsPanel({ alerts, k8sStale, appStale, appConfigured, onAck, onResolve }) {
  const open = alerts.filter((a) => !a.resolvedAt);
  const recentlyResolved = alerts.filter((a) => a.resolvedAt);

  if (open.length === 0 && recentlyResolved.length === 0 && !k8sStale && !(appConfigured && appStale)) return null;

  return (
    <div className="ow-node-card" style={{ marginBottom: 14 }}>
      <div className="ow-node-header">
        <div className="ow-node-name"><Bell size={16} /> Alerts</div>
        {open.length > 0 && <span className="ow-badge critical">{open.length} open</span>}
      </div>

      {k8sStale && (
        <div className="ow-coverage-message informational" style={{ marginTop: 0 }}>
          Workload, node and job alerting is paused: the Kubernetes tier is stale.
        </div>
      )}
      {appConfigured && appStale && (
        <div className="ow-coverage-message informational">
          Camera, Point, accessory, license and service alerting is paused: the app tier is stale.
        </div>
      )}

      {open.length === 0 ? (
        <div className="ow-form-help" style={{ marginTop: 8 }}>No open alerts.</div>
      ) : (
        <div className="ow-history-timeline" style={{ marginTop: 8 }}>
          {open.map((a) => {
            const members = Array.isArray(a.affectedDevices) ? a.affectedDevices : [];
            const remediation = a.metadata?.remediation;
            return (
              <div key={a.id} className="ow-alert-row">
                <span className={`ow-dot ${ALERT_SEVERITY_CLASS[a.severity] || 'unknown'}`} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="ow-workload-name">{a.title}</div>
                  <div className="ow-form-help">{a.message}</div>
                  {members.length > 0 && (
                    <div className="ow-form-help">Affects: {members.join(', ')}</div>
                  )}
                  {remediation && (
                    <div className="ow-form-help">
                      Remediation (operator runs this manually, never executed automatically): <code>{remediation}</code>
                    </div>
                  )}
                  <div className="ow-form-help">
                    {alertAge(a.triggeredAt)} ago{a.batchCount > 1 ? ` · x${a.batchCount}` : ''}
                    {a.acknowledgedAt ? ' · acknowledged' : ''}
                  </div>
                </div>
                <div className="ow-alert-actions">
                  {!a.acknowledgedAt && (
                    <button className="btn btn-secondary btn-sm" onClick={() => onAck(a.id)}><Check size={13} /> Ack</button>
                  )}
                  <button className="btn btn-secondary btn-sm" onClick={() => onResolve(a.id)}><CheckCircle2 size={13} /> Resolve</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── C2. Exceptions banner ──
   INFORMATIONAL and suppressed workloads are excluded entirely: they are
   reported, never alarmed. */
function ExceptionsBanner({ workloads, onJump, stale, lastSuccessAt }) {
  const exceptions = workloads.filter((w) => !w.suppressed && (w.health === 'CRITICAL' || w.health === 'WARNING'));
  const critical = exceptions.filter((w) => w.health === 'CRITICAL');
  const warning = exceptions.filter((w) => w.health === 'WARNING');

  // While collection is UNKNOWN or ERROR, a green all-clear is a claim about
  // the present that we cannot make, even when the stored data happens to
  // show zero exceptions right now. Every claim here must be timestamped,
  // not asserted, and never rendered in an alarm color either, since we do
  // not know if a stale problem has resolved or gotten worse.
  if (stale) {
    const detail = exceptions.length === 0
      ? `No problems were detected as of ${timeAgo(lastSuccessAt)}, but that reading is stale.`
      : `${exceptions.length} issue${exceptions.length === 1 ? '' : 's'} as of ${timeAgo(lastSuccessAt)}, but that reading is stale.`;
    return (
      <div className="ow-exceptions-banner unknown">
        <div className="ow-exceptions-clean unknown">
          <HelpCircle size={16} />
          <div>
            <div className="ow-exceptions-clean-title">Cannot determine current state</div>
            <div className="ow-exceptions-stale-detail">{detail}</div>
          </div>
        </div>
      </div>
    );
  }

  if (exceptions.length === 0) {
    return (
      <div className="ow-exceptions-banner">
        <div className="ow-exceptions-clean"><CheckCircle2 size={16} /> Nothing needs attention right now.</div>
      </div>
    );
  }

  const showInline = exceptions.length <= 6;

  return (
    <div className="ow-exceptions-banner has-issues">
      <div className="ow-exceptions-summary">
        {critical.length > 0 && (
          <span className="ow-exceptions-count critical"><HealthDot health="CRITICAL" /> {critical.length} critical</span>
        )}
        {warning.length > 0 && (
          <span className="ow-exceptions-count warning"><HealthDot health="WARNING" /> {warning.length} warning</span>
        )}
      </div>
      {showInline ? (
        <div className="ow-exceptions-list">
          {exceptions.map((w) => (
            <div className="ow-exceptions-item" key={`${w.namespace}/${w.workloadName}/${w.kind}`}>
              <HealthDot health={w.health} /> {w.workloadName} <span style={{ color: 'var(--text-tertiary)' }}>({w.namespace})</span>
            </div>
          ))}
        </div>
      ) : (
        <span className="ow-exceptions-jump" onClick={onJump}>Show all {exceptions.length} in the accordion below <ChevronRight size={12} /></span>
      )}
    </div>
  );
}

/* ── C3. Node cards ── */
/* ── Phase 4a: agent-sourced GPU card ──
   No threshold colouring (bar class "neutral" always) - there is no
   baseline for what counts as hot or loaded on this hardware, and 63C
   under load is entirely normal on a Quadro. Numbers and a bar, no
   judgment. */
function GpuCard({ gpu }) {
  const memPercent = gpu.memoryTotalMb ? (gpu.memoryUsedMb / gpu.memoryTotalMb) * 100 : null;
  return (
    <div className="ow-gpu-card">
      <div className="ow-gpu-card-name">GPU {gpu.index}{gpu.name ? ` · ${gpu.name}` : ''}</div>
      <div className="ow-metric-row">
        <div className="ow-metric-label-row">
          <span className="ow-metric-label">
            Utilization
            <InfoTooltip text="Percentage of GPU compute in use. The recognition pipeline runs here, so sustained high utilization with growing latency suggests the appliance is at capacity." />
          </span>
          <span className="ow-metric-value">{gpu.utilizationPercent != null ? `${gpu.utilizationPercent}%` : '—'}</span>
        </div>
        <div className="ow-metric-bar-track">
          <div className="ow-metric-bar-fill neutral" style={{ width: `${gpu.utilizationPercent ?? 0}%` }} />
        </div>
      </div>
      <div className="ow-metric-row">
        <div className="ow-metric-label-row">
          <span className="ow-metric-label">
            Memory
            <InfoTooltip text="GPU memory in use. Face recognition models load into GPU memory, so exhaustion here can stop recognition entirely." />
          </span>
          <span className="ow-metric-value">{gpu.memoryUsedMb != null && gpu.memoryTotalMb != null ? `${gpu.memoryUsedMb} / ${gpu.memoryTotalMb} MB` : '—'}</span>
        </div>
        <div className="ow-metric-bar-track">
          <div className="ow-metric-bar-fill neutral" style={{ width: `${memPercent ?? 0}%` }} />
        </div>
      </div>
      <div className="ow-gpu-card-footer">
        {gpu.temperatureC != null && <span>{gpu.temperatureC}°C</span>}
        {gpu.powerDrawWatts != null && <span>{gpu.powerDrawWatts.toFixed(1)} W</span>}
      </div>
    </div>
  );
}

function NodeCard({ node, stale, lastSuccessAt, hostTierStatus }) {
  const gpuInfo = node.gpuUnknown ? gpuUnavailableInfo({ hostBound: hostTierStatus.bound, hostOnline: hostTierStatus.online }) : null;
  const mounts = buildMountList(node.disks);
  return (
    <div className={`ow-node-card${stale ? ' ow-stale' : ''}`}>
      <div className="ow-node-header">
        <div>
          <div className="ow-node-name"><HealthDot health={stale ? 'UNKNOWN' : node.health} large /> {node.nodeName}</div>
          <div className="ow-node-version">{node.k8sVersion || 'version unknown'} · {node.osImage || 'OS unknown'}</div>
        </div>
        <span className={`ow-badge ${HEALTH_CLASS[stale ? 'UNKNOWN' : node.health] || 'unknown'}`}>{stale ? 'UNKNOWN' : node.health}</span>
      </div>

      {node.load1 != null && (
        <div className="ow-metric-row">
          <div className="ow-metric-label-row">
            <span className="ow-metric-label">
              Load
              <InfoTooltip text="System load divided by CPU cores. Raw load is meaningless without core count." />
            </span>
            <span className="ow-metric-value">{node.load1.toFixed(1)}{node.load1PerCore != null ? ` (${node.load1PerCore.toFixed(2)} per core)` : ''}</span>
          </div>
        </div>
      )}

      {node.memPercent != null && (
        <div className="ow-metric-row">
          <div className="ow-metric-label-row">
            <span className="ow-metric-label">Memory</span>
            <span className="ow-metric-value">
              {node.memPercent.toFixed(0)}% available
              {node.memTotalBytes != null ? ` of ${formatBytes(node.memTotalBytes)}` : ''}
            </span>
          </div>
          <div className="ow-metric-bar-track">
            <div className={`ow-metric-bar-fill ${barClass(100 - node.memPercent)}`} style={{ width: `${100 - node.memPercent}%` }} />
          </div>
        </div>
      )}

      {mounts.map((mount) => {
        const mountStale = mount.source === 'agent' && hostTierStatus.stale;
        // Only "/" is threshold-coloured (the documented upgrade risk);
        // every other mount stays neutral regardless of percent, per Task B.
        const cls = mount.mountpoint === '/' ? (mount.percent != null && mount.percent >= 80 ? 'warn' : 'ok') : 'neutral';
        return (
          <div className={`ow-metric-row${mountStale ? ' ow-mount-stale' : ''}`} key={mount.mountpoint}>
            <div className="ow-metric-label-row">
              <span className="ow-metric-label">
                Disk {mount.label}
                <span className="ow-mount-source">
                  {mount.source === 'agent' ? 'via agent' : mount.source === 'prometheus' ? 'via Prometheus' : ''}
                </span>
                <InfoTooltip text="Which source reported this. Root comes from the appliance's own metrics; other volumes require an agent installed on the appliance." />
              </span>
              <span className="ow-metric-value">{mount.percent != null ? `${mount.percent.toFixed(1)}%` : '—'}{mount.sizeBytes ? ` of ${formatBytes(mount.sizeBytes)}` : ''}</span>
            </div>
            <div className="ow-metric-bar-track">
              <div className={`ow-metric-bar-fill ${cls}`} style={{ width: `${mount.percent ?? 0}%` }} />
            </div>
          </div>
        );
      })}

      {node.gpuUnknown ? (
        <div className="ow-gpu-unavailable">
          <Cpu size={13} /> {gpuInfo?.message || 'GPU telemetry unavailable'}
          <InfoTooltip text="gpuUnknown can mean three different things: no host agent is bound to this instance (a configuration gap), the bound agent is offline (an outage), or the agent is online and simply found no GPU on this hardware." />
        </div>
      ) : node.gpus && node.gpus.length > 0 ? (
        <div className="ow-gpu-section">
          <div className="ow-gpu-grid">
            {node.gpus.map((gpu) => <GpuCard key={gpu.uuid || gpu.index} gpu={gpu} />)}
          </div>
          <div className="ow-gpu-source">via {node.gpuSource === 'agent' ? 'agent' : node.gpuSource === 'dcgm' ? 'DCGM' : node.gpuSource || 'unknown source'}</div>
        </div>
      ) : null}

      {stale && (
        <div className="ow-node-stale-footer">Values as of {timeAgo(lastSuccessAt)}</div>
      )}
    </div>
  );
}

/* ── Phase 4a, Task C: hardware identity ──
   Its own card, not folded into NodeCard: the node card already carries
   load, memory, N disk mounts and now GPU cards, and hardware facts are
   static rather than live metrics - a serial number does not become
   wrong because the agent went offline, so it must not visually gray out
   the way ow-stale grays out live metrics. Separating it into its own
   card makes that distinction obvious rather than requiring a reader to
   notice one field behaves differently from the rest of a shared card. */
function CopySerialButton({ serial }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(serial);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable (permissions, non-secure context) - no UI to show for this */
    }
  };
  return (
    <button type="button" className="btn btn-secondary btn-sm" onClick={onCopy}>
      {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? 'Copied' : `Copy serial: ${serial}`}
    </button>
  );
}

function HardwareCard({ node, hostCollectionLastSuccessAt }) {
  const fields = filterHardwareFields(node);
  if (fields.length === 0) return null;

  const warranty = buildWarrantyLink({ vendor: node.hwVendor, serial: node.hwProductSerial });

  return (
    <div className="ow-node-card ow-hardware-card">
      <div className="ow-node-header">
        <div className="ow-node-name">
          <HardDrive size={16} /> Hardware
        </div>
        {node.hwVendorSource && (
          <InfoTooltip text={
            node.hwVendorSource === 'uuid_pattern'
              ? 'Vendor determined from the DMI UUID pattern, since sys_vendor was blank on this unit.'
              : node.hwVendorSource === 'sys_vendor'
              ? 'Vendor read directly from the DMI sys_vendor field.'
              : 'Vendor could not be determined from any available DMI field.'
          } />
        )}
      </div>

      {fields.map((f) => (
        <div className="ow-metric-row" key={f.key}>
          <div className="ow-metric-label-row">
            <span className="ow-metric-label">{f.label}</span>
            <span className="ow-metric-value">{f.value}</span>
          </div>
        </div>
      ))}

      {warranty && (
        <div className="ow-hardware-warranty">
          {warranty.type === 'link' ? (
            <a href={warranty.url} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm">
              Warranty lookup
            </a>
          ) : (
            <CopySerialButton serial={warranty.serial} />
          )}
        </div>
      )}

      <div className="ow-node-stale-footer">
        Read at last host collection{hostCollectionLastSuccessAt ? `, ${timeAgo(hostCollectionLastSuccessAt)}` : ''}. Not live - hardware facts do not change because the agent went offline.
      </div>
    </div>
  );
}

/* ── C3b. App tier cards (Phase 2c) ──
   Rendered near the node cards, in the same grid, but gated on their own
   appStale/appConfigured signal rather than the Kubernetes-tier `stale`
   NodeCard above uses. The two tiers fail and recover independently; a
   healthy Kubernetes tier must never make a stale app tier look current. */
// Phase 2k: minutes since an ENABLE_FR completion, for the recovery
// progress line. Whole minutes, floor rather than round, so "0m" reads as
// "just now" rather than misleadingly implying a full minute has passed.
function minutesSince(dateStr) {
  if (!dateStr) return null;
  return Math.max(0, Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000));
}

function SabbathRecoveryProgress({ recovery }) {
  const pending = recovery.deviceCount - recovery.resumedCount;
  const minutes = minutesSince(recovery.startedAt);
  return (
    <div className="ow-sabbath-recovery-progress">
      {recovery.resumedCount} of {recovery.deviceCount} resumed, {pending} pending, {minutes}m since FR re-enabled
    </div>
  );
}

// Phase 2k, Task C: a small history list of past recovery windows, not a
// full audit page - shows the operator whether resumes have been clean
// lately without leaving the instance page.
function SabbathRecoveryHistoryCard({ events }) {
  if (!events || events.length === 0) return null;
  return (
    <div className="ow-node-card" style={{ marginBottom: 14 }}>
      <div className="ow-node-header">
        <div className="ow-node-name"><Moon size={16} /> Sabbath recovery history</div>
      </div>
      <div className="ow-history-timeline" style={{ marginTop: 8 }}>
        {events.map((e) => {
          const failed = Array.isArray(e.failedDeviceIds) ? e.failedDeviceIds.length : 0;
          const downtimeMin = e.slowestResumeSec != null ? Math.round(e.slowestResumeSec / 60) : null;
          return (
            <div key={e.id} className="ow-alert-row">
              <span className={`ow-dot ${failed > 0 ? 'warning' : 'healthy'}`} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="ow-workload-name">
                  {new Date(e.startedAt).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                  {' — '}{e.resumedCount} of {e.deviceCount} resumed
                  {failed > 0 ? `, ${failed} failed to resume` : ''}
                </div>
                <div className="ow-form-help">
                  {e.medianResumeSec != null ? `median resume ${Math.round(e.medianResumeSec / 60)}m` : 'no devices needed to resume'}
                  {downtimeMin != null ? `, slowest ${downtimeMin}m` : ''}
                  {` · grace period used: ${Math.round(e.gracePeriodSec / 60)}m`}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CameraSummaryCard({ instance, appStale, instanceId, sabbath }) {
  const active = instance.camerasActive;
  const total = instance.camerasTotal;
  const reconnecting = instance.camerasReconnecting;

  // Point counts come from GET /points' own counters block (stored under
  // licenseQuotas._meta.pointCounters, see the poller), never by filtering
  // cameras by streamType, and are never folded into the camera
  // reconnecting count above: Points and cameras are different devices
  // with different failure modes, and mixing the numbers would make both
  // meaningless. "error" is a Point being actively unhealthy;
  // "reconnecting" is included too since a Point mid-reconnect is not yet
  // a confirmed failure but is still worth surfacing.
  const pointCounters = instance.licenseQuotas?._meta?.pointCounters || null;
  const pointsActive = pointCounters?.active ?? null;
  const pointsTotal = pointCounters?.total ?? null;
  const pointsNeedingAttention = (pointCounters?.error ?? 0) + (pointCounters?.reconnecting ?? 0);

  return (
    <div className={`ow-node-card${appStale ? ' ow-stale' : ''}`}>
      <div className="ow-node-header">
        <div>
          <div className="ow-node-name"><Video size={16} /> Cameras</div>
        </div>
      </div>

      <div className="ow-metric-row">
        <div className="ow-metric-label-row">
          <span className="ow-metric-label">Active</span>
          <span className="ow-metric-value">{active ?? '—'} of {total ?? '—'}</span>
        </div>
      </div>

      {/* Phase 2k: while Shabbat Mode deliberately disabled FR, offline
          cameras are not a fault - muted styling, never the red warning
          badge, per the "CCM should not alert on states CCM created"
          principle. Recovery progress takes over once FR re-enables and
          a resume window opens (sabbath.recovery), until every device is
          back or the grace period elapses and the window closes. */}
      {sabbath?.active ? (
        <div className="ow-sabbath-badge">
          <Moon size={13} /> {reconnecting > 0 ? `${reconnecting} cameras offline, ` : ''}Sabbath Mode active
          {sabbath.nextEnableFRAt
            ? ` — expected to resume ${new Date(sabbath.nextEnableFRAt).toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' })}`
            : ''}
        </div>
      ) : sabbath?.recovery ? (
        <SabbathRecoveryProgress recovery={sabbath.recovery} />
      ) : reconnecting > 0 ? (
        <div className="ow-camera-reconnecting-badge">
          <AlertTriangle size={13} /> {reconnecting} reconnecting
        </div>
      ) : total != null ? (
        <div className="ow-form-help">No cameras reconnecting</div>
      ) : null}

      {pointCounters && (
        <div className="ow-metric-row">
          <div className="ow-metric-label-row">
            <span className="ow-metric-label">Points</span>
            <span className="ow-metric-value">{pointsActive ?? '—'} of {pointsTotal ?? '—'} active</span>
          </div>
          {pointsNeedingAttention > 0 && (
            <div className="ow-camera-reconnecting-badge" style={{ marginTop: 6 }}>
              <AlertTriangle size={13} /> {pointsNeedingAttention} point{pointsNeedingAttention === 1 ? '' : 's'} need attention
            </div>
          )}
        </div>
      )}

      {appStale && <div className="ow-node-stale-footer">Values as of {timeAgo(instance.appLastSuccessAt)}</div>}

      <Link to={`/monitoring/onwatch/${instanceId}/cameras`} className="ow-camera-card-link">
        View all cameras and points <ChevronRight size={13} />
      </Link>
    </div>
  );
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86400000);
}

function QuotaBar({ label, used, quota, tooltip }) {
  if (quota == null) return null;
  const pct = quota > 0 ? Math.min((used ?? 0) / quota * 100, 100) : 0;
  const overQuota = quota > 0 && (used ?? 0) / quota >= 0.9;
  return (
    <div className="ow-metric-row">
      <div className="ow-metric-label-row">
        <span className="ow-metric-label">
          {label}
          {tooltip && <InfoTooltip text={tooltip} />}
        </span>
        <span className="ow-metric-value">{used ?? '—'} of {quota}</span>
      </div>
      <div className="ow-metric-bar-track">
        <div className={`ow-metric-bar-fill ${overQuota ? 'crit' : 'ok'}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// Label by what the thing is, not by Oosto's internal stream-type name:
// nobody outside the product knows what a "crop" is. quotaBasedFeatures is
// an open map (five keys observed live, vendor docs list two), so an
// unrecognized key still renders, with its raw name, rather than being
// dropped - see the fallback in the render loop below.
const QUOTA_LABELS = {
  MAX_STREAMS: 'Camera Streams',
  MAX_CROP_STREAMS: 'Point Streams', // "crop" is Oosto's internal name for a Point
  MAX_WATCHLIST_SIZE: 'Watchlist',
};

const QUOTA_TOOLTIPS = {
  MAX_STREAMS: 'Licensed camera streams in use against the permitted total.',
  MAX_CROP_STREAMS: 'Licensed Point streams in use. Points are the Oosto access control tablets, licensed separately from cameras.',
  MAX_WATCHLIST_SIZE: 'Enrolled subjects against the permitted maximum.',
};

// "Used" values per quota key. MAX_STREAMS/MAX_WATCHLIST_SIZE already have
// dedicated OnWatchInstance columns from Phase 2c; MAX_CROP_STREAMS has no
// column (no schema change for this fix), so its usage travels inside
// licenseQuotas._meta.usage instead, set by the poller from the license
// response's enabledCropsCount. A key with no usage source shows its quota
// alone, no "used of" bar.
function quotaUsage(instance, key) {
  if (key === 'MAX_STREAMS') return instance.licenseCamerasCount;
  if (key === 'MAX_WATCHLIST_SIZE') return instance.licenseSubjectsCount;
  return instance.licenseQuotas?._meta?.usage?.[key] ?? null;
}

/**
 * Prompt 2g-fix, Item 2: identifiedSuspectsRate / unidentifiedSuspectsRate
 * come from reid-grpc-server counters that carry no per-camera label -
 * confirmed live before building this. Instance-wide only, labelled as
 * such, and NEVER rendered on a camera page (a support engineer reading
 * an instance-wide number on one camera's page would wrongly conclude
 * that camera is responsible). K8s/Prometheus-tier sourced, so gated on
 * `stale`, the same staleness this page's other Prometheus-derived cards
 * (PipelineLatencyCard) already use, not appStale.
 */
function RecognitionCard({ instance, stale }) {
  const identified = instance.identifiedSuspectsRate;
  const unidentified = instance.unidentifiedSuspectsRate;
  if (identified == null && unidentified == null) return null;

  const total = (identified || 0) + (unidentified || 0);
  const matchedPct = total > 0 ? ((identified || 0) / total * 100).toFixed(0) : null;

  return (
    <div className={`ow-node-card${stale ? ' ow-stale' : ''}`}>
      <div className="ow-node-header">
        <div>
          <div className="ow-node-name">
            Recognition
            <InfoTooltip text="Matched an enrolled subject versus detected-but-not-matched, across the whole instance. A 5-minute rate, not a per-camera figure - no per-camera breakdown exists for this signal." />
          </div>
        </div>
        <span className="ow-form-help">instance-wide</span>
      </div>
      {stale ? (
        <div className="ow-form-help">Unknown while collection is stale.</div>
      ) : (
        <>
          <div className="ow-metric-row">
            <div className="ow-metric-label-row">
              <span className="ow-metric-label">Matched</span>
              <span className="ow-metric-value">{identified != null ? `${identified.toFixed(2)}/sec` : '—'}</span>
            </div>
          </div>
          <div className="ow-metric-row">
            <div className="ow-metric-label-row">
              <span className="ow-metric-label">Unknown</span>
              <span className="ow-metric-value">{unidentified != null ? `${unidentified.toFixed(2)}/sec` : '—'}</span>
            </div>
          </div>
          {matchedPct != null && (
            <div className="ow-form-help">{matchedPct}% matched, 5-minute average</div>
          )}
        </>
      )}
    </div>
  );
}

function LicenseCard({ instance, appStale }) {
  const daysLeft = daysUntil(instance.licenseExpiryDate);
  const expiryWarn = daysLeft != null && daysLeft < 30;
  const quotas = instance.licenseQuotas || {};
  // _meta is our own computed envelope (usage values, point counters),
  // never a real quota key; excluded from the render loop below.
  const quotaKeys = Object.keys(quotas).filter((k) => k !== '_meta');

  return (
    <div className={`ow-node-card${appStale ? ' ow-stale' : ''}`}>
      <div className="ow-node-header">
        <div>
          <div className="ow-node-name"><ShieldCheck size={16} /> License</div>
        </div>
        {expiryWarn && <span className="ow-badge warning">Expiring soon</span>}
      </div>

      {instance.licenseExpiryDate ? (
        <div className="ow-metric-row">
          <div className="ow-metric-label-row">
            <span className="ow-metric-label">Expiry</span>
            <span className={`ow-metric-value${expiryWarn ? ' ow-license-warn' : ''}`}>
              {daysLeft >= 0 ? `${daysLeft}d remaining` : `expired ${Math.abs(daysLeft)}d ago`}
            </span>
          </div>
          <div className="ow-form-help">{new Date(instance.licenseExpiryDate).toLocaleDateString()}</div>
        </div>
      ) : (
        <div className="ow-form-help">No expiry data yet</div>
      )}

      {quotaKeys.map((key) => (
        <QuotaBar
          key={key}
          label={QUOTA_LABELS[key] || key}
          tooltip={QUOTA_TOOLTIPS[key]}
          used={quotaUsage(instance, key)}
          quota={quotas[key]?.permittedQuota}
        />
      ))}

      {appStale && <div className="ow-node-stale-footer">Values as of {timeAgo(instance.appLastSuccessAt)}</div>}
    </div>
  );
}

// Phase 2g, Task A: same fix as OnWatchCameraDetail.jsx's copy - pad the
// y-domain instead of collapsing to [0, max], so a flat (or near-flat)
// series renders as a thin line near the bottom rather than a solid
// filled block. See that file's sparklineDomain for the full reasoning.
function sparklineDomain(data) {
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min;
  const magnitude = Math.max(Math.abs(max), Math.abs(min));
  const isFlat = range === 0 || range < magnitude * 0.2;
  if (!isFlat) return { domainMin: min, domainMax: max };
  const pad = Math.max(magnitude * 0.2, 0.001);
  let domainMin = min - pad;
  if (min >= 0 && domainMin < 0) domainMin = 0;
  return { domainMin, domainMax: max + pad };
}

// Ported from MdSwitchDetail.jsx's Sparkline, same per-file local-copy
// convention as timeAgo elsewhere in this codebase (no shared chart
// component module exists to import from instead).
function Sparkline({ data, color, width = 90, height = 24 }) {
  if (!data || data.length < 2) {
    return <div style={{ width, height, background: 'transparent' }} />;
  }
  const { domainMin, domainMax } = sparklineDomain(data);
  const span = domainMax - domainMin || 1;
  const pts = data.map((v, i) =>
    `${(i / (data.length - 1)) * width},${height - ((v - domainMin) / span) * (height - 2) - 1}`
  ).join(' ');
  const gradId = `latency-sg-${color.replace('#', '')}`;
  return (
    <svg width={width} height={height} style={{ display: 'block', overflow: 'visible' }}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <polyline points={`0,${height} ${pts} ${width},${height}`} fill={`url(#${gradId})`} stroke="none" />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

function formatSeconds(v) {
  if (v == null) return '—';
  if (v < 1) return `${Math.round(v * 1000)}ms`;
  return `${v.toFixed(2)}s`;
}

const STAGE_LABELS = {
  frame_to_kafka: 'Frame to Kafka', frame_to_socket: 'Frame to Socket', frame_to_device: 'Frame to Device',
  collate_to_kafka: 'Collate to Kafka', track_process: 'Track Processing', reid_process: 'Re-identification',
  reid_insert_channel: 'ReID Queue Insertion', search_query: 'Watchlist Search', process_search_query: 'Search Processing',
  crops_upload: 'Crop Upload', device_action: 'Accessory Action', socket_event: 'Socket Event',
  sql_query: 'Database Query', pipe_iteration: 'Pipeline Iteration',
  // Prompt 2i, Task A: matches the renamed pipeline_stream_latency
  // catalog key (was stream_latency, which collided with the per-camera
  // catalog entry of the same name).
  pipeline_stream_latency: 'Stream Latency (pipeline-wide)',
  collate_process: 'Collate Processing (avg)',
};

/**
 * Headline: the four end-to-end p95 values. Diagnostic: when frame-to-kafka
 * is high relative to collate-to-kafka, most of the delay is upstream of
 * collate (capture or detection), and this says so directly rather than
 * leaving the subtraction to the reader (Phase 2d, A7's explicit ask). Not
 * a severity judgment or a threshold: no "slow" cutoff is assumed, only
 * which leg carries more of the total, so this is compatible with "do not
 * add threshold colouring yet."
 *
 * Stale treatment follows `stale` (the Kubernetes-tier derivation), not
 * appStale: these values come from Prometheus via the same catalog batch
 * as nodes/workloads, not the app tier.
 */
// Phase 2g, Task D: same reasoning as the camera detail page's
// formatBaselineNote - never colored as a warning, withheld entirely
// when the backend reports insufficient history.
function formatLatencyBaselineNote(current, medianVal) {
  if (medianVal == null || medianVal === 0 || current == null) return null;
  const deltaPct = ((current - medianVal) / medianVal) * 100;
  if (Math.abs(deltaPct) < 10) return `typical ${formatSeconds(medianVal)}`;
  return `${Math.abs(Math.round(deltaPct))}% ${deltaPct > 0 ? 'above' : 'below'} typical (${formatSeconds(medianVal)})`;
}

function PipelineLatencyCard({ instance, stale, history, baseline }) {
  const [expanded, setExpanded] = useState(false);

  const frameToKafka = instance.latencyFrameToKafkaP95;
  const frameToSocket = instance.latencyFrameToSocketP95;
  const frameToDevice = instance.latencyFrameToDeviceP95;
  const collateToKafka = instance.latencyCollateToKafkaP95;

  const hasAnyValue = [frameToKafka, frameToSocket, frameToDevice, collateToKafka].some((v) => v != null);
  if (!hasAnyValue) return null;

  let upstreamNote = null;
  if (frameToKafka != null && collateToKafka != null && frameToKafka > 0) {
    const upstreamShare = Math.max(0, frameToKafka - collateToKafka) / frameToKafka;
    if (upstreamShare > 0.5) {
      upstreamNote = `${Math.round(upstreamShare * 100)}% of frame-to-kafka latency is upstream of collate (capture or detection), not the pipeline's tail end.`;
    }
  }

  const headline = [
    { key: 'frame_to_kafka', label: 'Frame → Kafka', value: frameToKafka, color: '#3B82F6' },
    { key: 'frame_to_socket', label: 'Frame → Socket', value: frameToSocket, color: '#8B5CF6' },
    { key: 'frame_to_device', label: 'Frame → Device', value: frameToDevice, color: '#F59E0B' },
    { key: 'collate_to_kafka', label: 'Collate → Kafka', value: collateToKafka, color: '#10B981' },
  ];

  const stageEntries = Object.entries(instance.latencyDetail || {});

  return (
    <div className={`ow-node-card ow-latency-card${stale ? ' ow-stale' : ''}`}>
      <div className="ow-node-header">
        <div><div className="ow-node-name"><Zap size={16} /> Pipeline Latency</div></div>
      </div>

      <div className="ow-latency-headline-grid">
        {headline.map((h) => (
          <div key={h.key} className="ow-latency-headline-item">
            <div className="ow-metric-label">{h.label}</div>
            <div className="ow-metric-value">{stale ? '—' : formatSeconds(h.value)}</div>
            {!stale && <Sparkline data={(history?.[`latency_${h.key}_p95`] || []).map((p) => p.value)} color={h.color} />}
            {!stale && h.key === 'frame_to_device' && baseline && !baseline.insufficientHistory && (
              <div className="ow-baseline-note">{formatLatencyBaselineNote(h.value, baseline.latencyP95Median)}</div>
            )}
            {!stale && h.key === 'frame_to_device' && baseline?.insufficientHistory && (
              <div className="ow-baseline-building">baseline building</div>
            )}
          </div>
        ))}
      </div>

      {upstreamNote && !stale && (
        <div className="ow-form-help" style={{ marginTop: 8 }}>{upstreamNote}</div>
      )}

      <div className="ow-metric-row" style={{ marginTop: 10 }}>
        <div className="ow-metric-label-row">
          <span className="ow-metric-label">Active streams</span>
          <span className="ow-metric-value">{stale ? '—' : (instance.activeStreams ?? '—')}</span>
        </div>
        <div className="ow-metric-label-row">
          <span className="ow-metric-label">Detections/sec</span>
          <span className="ow-metric-value">{stale ? '—' : (instance.detectionsPerSecond != null ? instance.detectionsPerSecond.toFixed(1) : '—')}</span>
        </div>
      </div>

      {stageEntries.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <button
            className="ow-accordion-header"
            style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '6px 0' }}
            onClick={() => setExpanded((e) => !e)}
          >
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <span className="ow-accordion-title">Stage breakdown</span>
            <span className="ow-accordion-count">{stageEntries.length}</span>
          </button>
          {expanded && (
            <div className="ow-latency-stage-table">
              <div className="ow-latency-stage-row ow-latency-stage-header">
                <span>Stage</span><span>p50</span><span>p95</span><span>p99</span>
              </div>
              {stageEntries.map(([key, d]) => (
                <div key={key} className="ow-latency-stage-row">
                  <span>{STAGE_LABELS[key] || key}</span>
                  {d.avg !== undefined ? (
                    <span style={{ gridColumn: '2 / span 3' }}>{Math.round(d.avg)}μs avg</span>
                  ) : (
                    <>
                      <span>{formatSeconds(d.p50)}</span>
                      <span>{formatSeconds(d.p95)}</span>
                      <span>{formatSeconds(d.p99)}</span>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {stale && <div className="ow-node-stale-footer">Values as of {timeAgo(instance.lastSuccessAt)}</div>}
    </div>
  );
}

/**
 * App tier state shown distinctly from the Kubernetes tier's own
 * CollectionStateBanner above it, never averaged into one status. A
 * support engineer needs to see "Kubernetes is fine, the app API is
 * broken" as two separate facts, not one blended color.
 */
function AppTierStatusLine({ instance, appStale, appConfigured }) {
  if (!appConfigured) return null;
  const hasError = !!instance.appLastError;
  const health = hasError ? 'CRITICAL' : appStale ? 'UNKNOWN' : 'HEALTHY';
  const label = hasError ? 'App tier: error' : appStale ? 'App tier: stale' : 'App tier: OK';

  return (
    <div className={`ow-app-tier-status ${HEALTH_CLASS[health]}`}>
      <HealthDot health={health} />
      <span className="ow-app-tier-status-label">{label}</span>
      {hasError ? (
        <span className="ow-form-help">{instance.appLastError}</span>
      ) : (
        <span className="ow-form-help">as of {timeAgo(instance.appLastSuccessAt)}</span>
      )}
    </div>
  );
}

/* ── Phase 4a, Task D: host-collection agent tier status ──
   A third independent tier alongside the k8s tier (CollectionStateBanner)
   and app tier (AppTierStatusLine) above. Unbound is a configuration
   state most instances legitimately sit in - it is deliberately NOT
   rendered as an error, unlike AppTierStatusLine's "not configured"
   which simply returns null. A host agent is opt-in extra detail, so its
   absence gets an explanatory line rather than being hidden entirely. */
function HostTierStatusLine({ instance, hostTierStatus }) {
  if (!hostTierStatus.bound) {
    return (
      <div className="ow-app-tier-status unknown">
        <HealthDot health="UNKNOWN" />
        <span className="ow-app-tier-status-label">
          Host agent: not configured
          <InfoTooltip text="An agent installed on the appliance itself, which reports GPU, full disk and hardware detail that cannot be read remotely. Separate from the tunnel agent that carries network traffic." />
        </span>
        <span className="ow-form-help">GPU, full disk and hardware detail require an agent installed on the appliance.</span>
      </div>
    );
  }

  const health = !hostTierStatus.online ? 'CRITICAL' : hostTierStatus.stale ? 'UNKNOWN' : 'HEALTHY';
  const label = !hostTierStatus.online ? 'Host agent: offline' : hostTierStatus.stale ? 'Host agent: stale' : 'Host agent: OK';

  return (
    <div className={`ow-app-tier-status ${HEALTH_CLASS[health]}`}>
      <HealthDot health={health} />
      <span className="ow-app-tier-status-label">
        {label}
        <InfoTooltip text="An agent installed on the appliance itself, which reports GPU, full disk and hardware detail that cannot be read remotely. Separate from the tunnel agent that carries network traffic." />
      </span>
      <span className="ow-form-help">
        {instance.hostAgentName || instance.hostAgentHostname || instance.hostAgentId}
        {' · '}
        {instance.hostCollectionLastSuccessAt ? `last collection ${timeAgo(instance.hostCollectionLastSuccessAt)}` : 'never collected'}
      </span>
    </div>
  );
}

/* ── C4. Workload accordion ── */
function WorkloadRow({ workload, stale, instanceId, appStale }) {
  const navigate = useNavigate();
  const health = stale ? 'UNKNOWN' : workload.health;
  const hasFraction = workload.replicasDesired != null && workload.replicasReady != null;
  const phases = [];
  if (workload.podsPending) phases.push(`${workload.podsPending} pending`);
  if (workload.podsFailed) phases.push(`${workload.podsFailed} failed`);
  if (workload.podsSucceeded) phases.push(`${workload.podsSucceeded} succeeded`);

  const openDetail = () => {
    navigate(`/monitoring/onwatch/${instanceId}/workloads/${encodeURIComponent(workload.namespace)}/${encodeURIComponent(workload.workloadName)}?kind=${workload.kind}`);
  };

  return (
    <div
      className={`ow-workload-row${workload.suppressed ? ' suppressed' : ''}${stale ? ' ow-stale' : ''}`}
      onClick={openDetail}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDetail(); } }}
      role="button"
      tabIndex={0}
    >
      <HealthDot health={health} />
      <span className="ow-workload-name">{workload.workloadName}</span>
      <span className="ow-kind-badge">{workload.kind}</span>
      <div className="ow-workload-meta">
        {hasFraction && <span className="ow-workload-fraction">{workload.replicasReady}/{workload.replicasDesired}</span>}
        {workload.restarts1h > 0 && <span className="ow-workload-restarts">{workload.restarts1h.toFixed(0)} restarts/1h</span>}
        {phases.length > 0 && <span className="ow-workload-phases">{phases.join(', ')}</span>}
        {workload.suppressed && <span className="ow-suppressed-badge" title={workload.healthNote || ''}>suppressed</span>}
        {/* Consul health merge decision (Phase 2c): shown as an additional
            badge on the matching workload row rather than a second, easily
            contradictory row in a separate list. Never claims a live color
            while the app tier itself is stale - same Never False Green
            treatment as everything else on this page. */}
        {workload.consulHealth && (
          <span
            className={`ow-consul-badge ${appStale ? 'unknown' : (HEALTH_CLASS[workload.consulHealth] || 'unknown')}`}
            title={`Consul: ${workload.consulStatus || workload.consulHealth}${appStale ? ' (stale)' : ''}`}
          >
            Consul
          </span>
        )}
      </div>
    </div>
  );
}

function AccordionSection({ category, workloads, defaultOpen, stale, sectionRef, searchActive, instanceId, appStale }) {
  // null means "no manual override yet": while a search is active, a
  // section with matching results auto-expands even if it defaults closed
  // (Unclassified, Monitoring), since a collapsed match a user cannot see
  // defeats the point of the search box. A manual click always wins over
  // search state afterward, in either direction.
  const [userOpen, setUserOpen] = useState(null);
  const open = userOpen !== null ? userOpen : (defaultOpen || searchActive);
  const worst = workloads.reduce((acc, w) => (HEALTH_RANK[w.health] > HEALTH_RANK[acc] ? w.health : acc), 'HEALTHY');

  return (
    <div className="ow-accordion-section" ref={sectionRef}>
      <div className="ow-accordion-header" onClick={() => setUserOpen(!open)}>
        <HealthDot health={stale ? 'UNKNOWN' : worst} />
        <span className="ow-accordion-title">{CATEGORY_LABEL[category] || category}</span>
        <span className="ow-accordion-count">{workloads.length}</span>
        <ChevronRight size={16} className={`ow-accordion-chevron${open ? ' open' : ''}`} />
      </div>
      {open && (
        <div className="ow-accordion-body">
          {workloads.map((w) => (
            <WorkloadRow key={`${w.namespace}/${w.workloadName}/${w.kind}`} workload={w} stale={stale} instanceId={instanceId} appStale={appStale} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── C6. Log bundle wizard ──
   "Collect logs" is a support-workflow action, not a health readout: it
   reads pods/log live from Kubernetes, so it can still run even while
   collectionState is UNKNOWN or ERROR. What it CANNOT do in that state is
   promise a fresh cluster-state.json, since that file is built from our
   own stored poll data - so the stale note below is scoped to that one
   file, not to the whole feature. */
const TIME_WINDOWS = [
  { label: '15 minutes', value: 900 },
  { label: '1 hour', value: 3600 },
  { label: '6 hours', value: 21600 },
  { label: '24 hours', value: 86400 },
];
const TAIL_LINE_OPTIONS = [1000, 5000, 20000];
const MAX_WORKLOADS_PER_BUNDLE = 20;

function formatLogBytes(bytes) {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}

function workloadKey(w) {
  return `${w.namespace}|${w.workloadName}|${w.kind}`;
}

function toDatetimeLocalValue(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function WizardWorkloadRow({ workload, checked, onToggle }) {
  return (
    <label className="ow-wizard-workload-row">
      <input type="checkbox" checked={checked} onChange={onToggle} />
      <HealthDot health={workload.health} />
      <span className="ow-wizard-workload-name">{workload.workloadName}</span>
      <span className="ow-kind-badge">{workload.kind}</span>
      {(workload.restarts1h > 0 || workload.restarts24h > 0) && (
        <span className="ow-wizard-restarts">
          {workload.restarts1h > 0 ? `${workload.restarts1h.toFixed(0)}/1h` : ''}
          {workload.restarts24h > 0 ? ` ${workload.restarts24h.toFixed(0)}/24h` : ''}
        </span>
      )}
    </label>
  );
}

function LogWizardStep1({ workloads, selected, toggleWorkload, criticalCandidates, search, setSearch, groupedForSearch, estimate, estimating }) {
  return (
    <div>
      <div className="ow-wizard-summary-row">
        <span>{selected.size} of {MAX_WORKLOADS_PER_BUNDLE} selected</span>
        {selected.size >= MAX_WORKLOADS_PER_BUNDLE && <span className="ow-wizard-cap-warning">Cap reached</span>}
        <span className="ow-wizard-estimate">
          {estimating ? 'Estimating…' : estimate ? `~${formatLogBytes(estimate.estimatedBytes)}, ${estimate.containerCount} container fetches` : ''}
        </span>
      </div>

      {criticalCandidates.length > 0 && (
        <div className="ow-wizard-section">
          <div className="ow-wizard-section-title">Critical services not yet selected</div>
          <div className="ow-wizard-workload-list">
            {criticalCandidates.map((w) => (
              <WizardWorkloadRow key={workloadKey(w)} workload={w} checked={false} onToggle={() => toggleWorkload(w)} />
            ))}
          </div>
        </div>
      )}

      <div className="ow-wizard-section">
        <div className="ow-search-wrap">
          <Search size={14} />
          <input placeholder="Search all workloads..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="ow-wizard-grouped-list">
          {CATEGORY_ORDER.map((cat) => {
            const list = groupedForSearch[cat];
            if (!list || list.length === 0) return null;
            return (
              <div key={cat} className="ow-wizard-category-group">
                <div className="ow-wizard-category-label">{CATEGORY_LABEL[cat]}</div>
                {list.map((w) => (
                  <WizardWorkloadRow key={workloadKey(w)} workload={w} checked={selected.has(workloadKey(w))} onToggle={() => toggleWorkload(w)} />
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const COVERAGE_LABEL = { full: 'Fully covered', partial: 'Partially covered', none: 'Not covered', unknown: 'Unknown' };
const COVERAGE_CLASS = { full: 'full', partial: 'partial', none: 'none', unknown: 'unknown' };

function CoverageBadge({ status }) {
  return <span className={`ow-coverage-badge ${COVERAGE_CLASS[status] || 'unknown'}`}>{COVERAGE_LABEL[status] || 'Unknown'}</span>;
}

/* ── Phase 4b, Task E: observed lookback per service ──
   "cv-engine: 6 weeks available" and "ldap-service: 11 months available"
   on the same appliance is normal - retention is space-derived, not a
   policy number, so this is measured from the agent's own inventory, not
   asserted. Whole units only (weeks/months), since day-level precision
   here would read as more certain than a rotation-boundary estimate
   actually is. */
function formatLookback(oldestMs, newestMs) {
  if (!oldestMs || !newestMs) return null;
  const days = Math.max(0, Math.round((newestMs - oldestMs) / 86400000));
  if (days < 14) return `${days} day${days === 1 ? '' : 's'} available`;
  if (days < 60) return `${Math.round(days / 7)} weeks available`;
  return `${Math.round(days / 30)} months available`;
}

function CoverageSourceLabel({ source }) {
  if (!source) return null;
  const label = source === 'kubernetes+agent' ? 'Kubernetes API + host agent'
    : source === 'kubernetes' ? 'Kubernetes API'
    : source === 'agent' ? 'Host agent' : source;
  return <span className="ow-wizard-coverage-source">{label}</span>;
}

function CoverageWorkloadRow({ w, onUseNearestRange }) {
  const lookback = w.agentCoverage ? formatLookback(w.agentCoverage.oldestMs, w.agentCoverage.newestMs) : null;
  return (
    <div className="ow-wizard-coverage-row">
      <div className="ow-wizard-coverage-row-main">
        <CoverageBadge status={w.status} />
        <span className="ow-wizard-coverage-name">{w.workloadName}</span>
        <CoverageSourceLabel source={w.source} />
      </div>
      {w.reason && <div className="ow-wizard-coverage-reason">{w.reason}</div>}
      {w.agentNote && <div className="ow-wizard-coverage-agent-note">{w.agentNote}</div>}
      {lookback && <div className="ow-wizard-coverage-lookback">{w.workloadName}: {lookback}</div>}
      {w.status !== 'full' && w.nearestAvailableRange && (
        <button
          type="button"
          className="ow-wizard-coverage-nearest-btn"
          onClick={() => onUseNearestRange(w.nearestAvailableRange)}
        >
          Use nearest available range instead ({new Date(w.nearestAvailableRange.from).toLocaleString()} to {new Date(w.nearestAvailableRange.to).toLocaleString()})
        </button>
      )}
    </div>
  );
}

/* ── Phase 4b, Task E: free space on the log volume ──
   The one piece of preventative information in this whole feature:
   retention shrinks silently as the volume fills, so this is worth
   surfacing before someone actually needs the history, not only once a
   request already came back short. Quiet below the warning threshold -
   this is not a health indicator, it does not belong graduating to a red
   banner the way collection state does. */
const LOG_VOLUME_LOW_SPACE_THRESHOLD = 0.15; // fraction free

function LogVolumeFreeSpaceNote({ hostAgent }) {
  if (!hostAgent || !hostAgent.bound || !hostAgent.online) return null;
  if (hostAgent.freeBytes == null || hostAgent.totalBytes == null || hostAgent.totalBytes === 0) return null;
  const fraction = hostAgent.freeBytes / hostAgent.totalBytes;
  if (fraction > LOG_VOLUME_LOW_SPACE_THRESHOLD) return null;
  return (
    <div className="ow-wizard-coverage-lowspace">
      <AlertTriangle size={13} />
      {formatLogBytes(hostAgent.freeBytes)} free on the log volume ({Math.round(fraction * 100)}%). Historical lookback shrinks as it fills.
    </div>
  );
}

function LogWizardStep2({
  mode, setMode,
  sinceSeconds, setSinceSeconds,
  pitFrom, setPitFrom, pitTo, setPitTo,
  tailLines, setTailLines, includeClusterState, setIncludeClusterState,
  estimate, estimating, coverage, coverageLoading, selectedCount, stale,
}) {
  return (
    <div>
      <div className="ow-wizard-section">
        <div className="ow-wizard-section-title">When</div>
        <div className="ow-chip-row">
          <button type="button" className={`ow-chip${mode === 'recent' ? ' active' : ''}`} onClick={() => setMode('recent')}>Recent</button>
          <button type="button" className={`ow-chip${mode === 'pointInTime' ? ' active' : ''}`} onClick={() => setMode('pointInTime')}>Point in time</button>
        </div>
      </div>

      {mode === 'recent' && (
        <div className="ow-wizard-section">
          <div className="ow-wizard-section-title">Time window</div>
          <div className="ow-chip-row">
            {TIME_WINDOWS.map((w) => (
              <button key={w.value} type="button" className={`ow-chip${sinceSeconds === w.value ? ' active' : ''}`} onClick={() => setSinceSeconds(w.value)}>{w.label}</button>
            ))}
          </div>
        </div>
      )}

      {mode === 'pointInTime' && (
        <div className="ow-wizard-section">
          <div className="ow-wizard-section-title">Range</div>
          <div className="ow-wizard-datetime-row">
            <label>
              From
              <input type="datetime-local" className="form-input" value={pitFrom} onChange={(e) => setPitFrom(e.target.value)} />
            </label>
            <label>
              To
              <input type="datetime-local" className="form-input" value={pitTo} onChange={(e) => setPitTo(e.target.value)} />
            </label>
          </div>
          <div className="ow-wizard-coverage">
            {coverageLoading && <span className="ow-wizard-coverage-loading"><Loader2 size={12} className="spin" /> Checking coverage…</span>}
            {!coverageLoading && coverage && <LogVolumeFreeSpaceNote hostAgent={coverage.hostAgent} />}
            {!coverageLoading && coverage && coverage.workloads.map((w) => (
              <CoverageWorkloadRow
                key={`${w.namespace}|${w.workloadName}`}
                w={w}
                onUseNearestRange={(range) => {
                  setPitFrom(toDatetimeLocalValue(new Date(range.from)));
                  setPitTo(toDatetimeLocalValue(new Date(range.to)));
                }}
              />
            ))}
            {!coverageLoading && !coverage && (
              <div className="ow-wizard-coverage-empty">Pick a valid range to check coverage before generating.</div>
            )}
          </div>
        </div>
      )}

      <div className="ow-wizard-section">
        <div className="ow-wizard-section-title">Tail lines per container</div>
        <div className="ow-chip-row">
          {TAIL_LINE_OPTIONS.map((n) => (
            <button key={n} type="button" className={`ow-chip${tailLines === n ? ' active' : ''}`} onClick={() => setTailLines(n)}>{n.toLocaleString()}</button>
          ))}
        </div>
      </div>
      <label className="ow-wizard-checkbox-row">
        <input type="checkbox" checked={includeClusterState} onChange={(e) => setIncludeClusterState(e.target.checked)} />
        Include cluster state snapshot (cluster-state.json)
      </label>
      {includeClusterState && stale && (
        <div className="ow-wizard-stale-note">
          Collection is not current right now. cluster-state.json will reflect our last known
          snapshot, not live state - it is timestamped so it cannot be mistaken for current data.
        </div>
      )}
      <div className="ow-wizard-estimate-restated">
        {selectedCount} service{selectedCount === 1 ? '' : 's'} ·{' '}
        {estimating ? 'estimating…' : estimate ? `~${formatLogBytes(estimate.estimatedBytes)} across ${estimate.containerCount} container fetches` : 'no estimate yet'}
      </div>
    </div>
  );
}

function LogWizardStep3({ job, generating, onGenerate, onDownload, selectedCount, coverage }) {
  const uncovered = coverage ? coverage.workloads.filter((w) => w.status !== 'full') : [];
  return (
    <div className="ow-wizard-generate">
      {!job && !generating && (
        <div>
          <p>Ready to collect logs for {selectedCount} service{selectedCount === 1 ? '' : 's'}.</p>
          {uncovered.length > 0 && (
            <div className="ow-wizard-coverage-warning">
              <AlertTriangle size={16} />
              <span>
                {uncovered.length} of {coverage.workloads.length} service{coverage.workloads.length === 1 ? '' : 's'} may not fully
                reach the requested start time. The bundle will still include whatever the API has, honestly labelled in
                manifest.json, not silently trimmed.
              </span>
            </div>
          )}
          <button type="button" className="btn btn-accent" onClick={onGenerate}>Generate bundle</button>
        </div>
      )}
      {job && (job.status === 'pending' || job.status === 'running') && (
        <div className="ow-wizard-progress">
          <Loader2 size={20} className="spin" />
          <p>Fetching logs… {job.progress?.total > 0 ? `${job.progress.done} of ${job.progress.total} containers` : 'starting...'}</p>
        </div>
      )}
      {job && job.status === 'ready' && (
        <div className="ow-wizard-ready">
          <CheckCircle2 size={24} className="ow-wizard-ready-icon" />
          <p>Bundle ready{job.size != null ? ` (${formatLogBytes(job.size)})` : ''}.</p>
          <button type="button" className="btn btn-accent" onClick={onDownload}><Download size={14} /> Download</button>
          <p className="ow-wizard-note">The bundle is deleted from the server once downloaded. Save it before closing this window.</p>
        </div>
      )}
      {job && job.status === 'downloaded' && (
        <div className="ow-wizard-ready">
          <CheckCircle2 size={24} className="ow-wizard-ready-icon" />
          <p>Downloaded. The server copy has been deleted.</p>
        </div>
      )}
      {job && job.status === 'failed' && (
        <div className="ow-wizard-failed">
          <AlertCircle size={20} className="ow-wizard-failed-icon" />
          <p>{job.error || 'Bundle generation failed.'}</p>
          <button type="button" className="btn btn-secondary" onClick={onGenerate}><RefreshCw size={14} /> Retry</button>
        </div>
      )}
    </div>
  );
}

function LogBundleWizard({ instanceId, workloads, stale, preselectWorkloadName, onClose }) {
  const [step, setStep] = useState(1);
  const [selected, setSelected] = useState(() => {
    const initial = new Set();
    // Phase 2f: a camera problem's log bundle wants both cv-engine and
    // camera-service, so this accepts a comma-separated list, not just one
    // name - additive, a single name still works exactly as before.
    const preselectNames = preselectWorkloadName ? preselectWorkloadName.split(',').map((n) => n.trim()).filter(Boolean) : [];
    for (const w of workloads) {
      if (w.health === 'CRITICAL' || w.health === 'WARNING') initial.add(workloadKey(w));
      if (preselectNames.includes(w.workloadName)) initial.add(workloadKey(w));
    }
    return initial;
  });
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState('recent'); // 'recent' | 'pointInTime'
  const [sinceSeconds, setSinceSeconds] = useState(3600);
  const [pitFrom, setPitFrom] = useState(() => toDatetimeLocalValue(new Date(Date.now() - 3600000)));
  const [pitTo, setPitTo] = useState(() => toDatetimeLocalValue(new Date()));
  const [tailLines, setTailLines] = useState(5000);
  const [includeClusterState, setIncludeClusterState] = useState(true);
  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [coverage, setCoverage] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [job, setJob] = useState(null);
  const [generating, setGenerating] = useState(false);
  const jobPollRef = useRef(null);

  const selectedWorkloads = useMemo(
    () => workloads.filter((w) => selected.has(workloadKey(w))),
    [workloads, selected],
  );

  const toggleWorkload = (w) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const key = workloadKey(w);
      if (next.has(key)) next.delete(key);
      else if (next.size < MAX_WORKLOADS_PER_BUNDLE) next.add(key);
      return next;
    });
  };

  // Debounced live estimate: fires on selection or tail-line changes so
  // both wizard steps can show an up to date projected size without a
  // request per keystroke/click.
  useEffect(() => {
    if (selectedWorkloads.length === 0) {
      setEstimate(null);
      return undefined;
    }
    const body = selectedWorkloads.map((w) => ({ namespace: w.namespace, workloadName: w.workloadName, kind: w.kind }));
    const timer = setTimeout(async () => {
      setEstimating(true);
      try {
        const qs = new URLSearchParams({ workloads: JSON.stringify(body), tailLines: String(tailLines) });
        const res = await fetchWithTenant(`/api/onwatch/instances/${instanceId}/logs/estimate?${qs.toString()}`);
        if (res.ok) setEstimate(await res.json());
      } catch {
        // Estimate is advisory only; a failure here should not block the wizard.
      } finally {
        setEstimating(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [selectedWorkloads, tailLines, instanceId]);

  // Coverage check: point-in-time mode only. The user picks a range and
  // never picks a source, so this is what decides, per workload, whether
  // the Kubernetes API can actually reach that far back before the user
  // commits to generating.
  useEffect(() => {
    if (mode !== 'pointInTime' || selectedWorkloads.length === 0) {
      setCoverage(null);
      return undefined;
    }
    const fromDate = new Date(pitFrom);
    const toDate = new Date(pitTo);
    if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime()) || fromDate >= toDate) {
      setCoverage(null);
      return undefined;
    }
    const body = selectedWorkloads.map((w) => ({ namespace: w.namespace, workloadName: w.workloadName, kind: w.kind }));
    const timer = setTimeout(async () => {
      setCoverageLoading(true);
      try {
        const res = await fetchWithTenant(`/api/onwatch/instances/${instanceId}/logs/coverage`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ workloads: body, from: fromDate.toISOString(), to: toDate.toISOString() }),
        });
        if (res.ok) setCoverage(await res.json());
      } catch {
        // Coverage is advisory pre-generation; a failure here should not block the wizard.
      } finally {
        setCoverageLoading(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [mode, selectedWorkloads, pitFrom, pitTo, instanceId]);

  useEffect(() => () => {
    if (jobPollRef.current) clearInterval(jobPollRef.current);
  }, []);

  const criticalCandidates = workloads.filter((w) => w.critical && !selected.has(workloadKey(w)));

  const filteredForSearch = useMemo(() => {
    if (!search.trim()) return workloads;
    const q = search.trim().toLowerCase();
    return workloads.filter((w) => w.workloadName.toLowerCase().includes(q));
  }, [workloads, search]);

  const groupedForSearch = useMemo(() => {
    const byCategory = {};
    for (const cat of CATEGORY_ORDER) byCategory[cat] = [];
    for (const w of filteredForSearch) {
      const cat = CATEGORY_ORDER.includes(w.category) ? w.category : 'UNCLASSIFIED';
      byCategory[cat].push(w);
    }
    return byCategory;
  }, [filteredForSearch]);

  const pollJob = (jobId) => {
    if (jobPollRef.current) clearInterval(jobPollRef.current);
    jobPollRef.current = setInterval(async () => {
      try {
        const res = await fetchWithTenant(`/api/onwatch/instances/${instanceId}/logs/bundle/${jobId}`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
        setJob(data);
        if (data.status === 'ready' || data.status === 'failed') {
          clearInterval(jobPollRef.current);
          jobPollRef.current = null;
          setGenerating(false);
        }
      } catch (err) {
        setJob((prev) => ({ ...(prev || {}), status: 'failed', error: err.message }));
        clearInterval(jobPollRef.current);
        jobPollRef.current = null;
        setGenerating(false);
      }
    }, 1500);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setJob(null);
    try {
      const body = mode === 'pointInTime'
        ? {
          mode: 'pointInTime',
          workloads: selectedWorkloads.map((w) => ({ namespace: w.namespace, workloadName: w.workloadName, kind: w.kind })),
          from: new Date(pitFrom).toISOString(),
          to: new Date(pitTo).toISOString(),
          tailLines,
          includeClusterState,
        }
        : {
          mode: 'recent',
          workloads: selectedWorkloads.map((w) => ({ namespace: w.namespace, workloadName: w.workloadName, kind: w.kind })),
          sinceSeconds,
          tailLines,
          includeClusterState,
        };
      const res = await fetchWithTenant(`/api/onwatch/instances/${instanceId}/logs/bundle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setJob({ jobId: data.jobId, status: data.status, progress: { done: 0, total: 0 }, size: null, error: null });
      pollJob(data.jobId);
    } catch (err) {
      setJob({ jobId: null, status: 'failed', progress: null, size: null, error: err.message });
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    if (!job?.jobId) return;
    try {
      const res = await fetchWithTenant(`/api/onwatch/instances/${instanceId}/logs/bundle/${job.jobId}/download`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `onwatch-logs-${job.jobId}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      // The server deletes its copy as soon as this download completes;
      // reflect that immediately so a second click reads as impossible
      // rather than silently doing nothing.
      setJob((prev) => ({ ...prev, status: 'downloaded' }));
    } catch (err) {
      setJob((prev) => ({ ...prev, error: `Download failed: ${err.message}` }));
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content ow-log-wizard" style={{ maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3><FileArchive size={16} /> Collect logs{step > 1 ? ` · Step ${step} of 3` : ''}</h3>
          <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">
          {step === 1 && (
            <LogWizardStep1
              workloads={workloads}
              selected={selected}
              toggleWorkload={toggleWorkload}
              criticalCandidates={criticalCandidates}
              search={search}
              setSearch={setSearch}
              groupedForSearch={groupedForSearch}
              estimate={estimate}
              estimating={estimating}
            />
          )}
          {step === 2 && (
            <LogWizardStep2
              mode={mode} setMode={setMode}
              sinceSeconds={sinceSeconds} setSinceSeconds={setSinceSeconds}
              pitFrom={pitFrom} setPitFrom={setPitFrom}
              pitTo={pitTo} setPitTo={setPitTo}
              tailLines={tailLines} setTailLines={setTailLines}
              includeClusterState={includeClusterState} setIncludeClusterState={setIncludeClusterState}
              estimate={estimate} estimating={estimating}
              coverage={coverage} coverageLoading={coverageLoading}
              selectedCount={selectedWorkloads.length}
              stale={stale}
            />
          )}
          {step === 3 && (
            <LogWizardStep3
              job={job}
              generating={generating}
              onGenerate={handleGenerate}
              onDownload={handleDownload}
              selectedCount={selectedWorkloads.length}
              coverage={mode === 'pointInTime' ? coverage : null}
            />
          )}
        </div>
        <div className="modal-footer">
          {step > 1 && step < 3 && (
            <button type="button" className="btn btn-secondary" onClick={() => setStep((s) => s - 1)}><ChevronLeft size={14} /> Back</button>
          )}
          {step === 1 && (
            <button type="button" className="btn btn-accent" onClick={() => setStep(2)} disabled={selectedWorkloads.length === 0}>
              Next ({selectedWorkloads.length} selected)
            </button>
          )}
          {step === 2 && (
            <button type="button" className="btn btn-accent" onClick={() => setStep(3)}>Next</button>
          )}
          {step === 3 && (
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function OnWatchHealth() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [instance, setInstance] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [workloads, setWorkloads] = useState([]);
  const [orphanServices, setOrphanServices] = useState([]);
  const [latencyHistory, setLatencyHistory] = useState({});
  const [latencyBaseline, setLatencyBaseline] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [sabbath, setSabbath] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [polling, setPolling] = useState(false);
  const [search, setSearch] = useState('');
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [showLogWizard, setShowLogWizard] = useState(false);
  // Set when navigated here from the workload detail page's "Collect logs"
  // button, e.g. /monitoring/onwatch/:id?collectLogs=cv-engine. Consumed
  // once, then stripped from the URL so a refresh does not reopen it.
  const [wizardPreselect, setWizardPreselect] = useState(null);

  useEffect(() => {
    const preselect = searchParams.get('collectLogs');
    if (preselect) {
      setWizardPreselect(preselect);
      setShowLogWizard(true);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const timerRef = useRef(null);
  const accordionRefs = useRef({});

  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [instRes, nodesRes, workloadsRes, servicesRes, latencyHistoryRes, latencyBaselineRes, alertsRes, sabbathRes] = await Promise.all([
        fetchWithTenant(`/api/onwatch/instances/${id}`),
        fetchWithTenant(`/api/onwatch/instances/${id}/nodes`),
        fetchWithTenant(`/api/onwatch/instances/${id}/workloads`),
        fetchWithTenant(`/api/onwatch/instances/${id}/services`),
        fetchWithTenant(`/api/onwatch/instances/${id}/latency/history`),
        fetchWithTenant(`/api/onwatch/instances/${id}/latency/baseline`),
        fetchWithTenant(`/api/onwatch/instances/${id}/alerts`),
        fetchWithTenant(`/api/onwatch/instances/${id}/sabbath-status`),
      ]);
      if (!instRes.ok) throw new Error(`HTTP ${instRes.status}`);
      if (!nodesRes.ok) throw new Error(`HTTP ${nodesRes.status}`);
      if (!workloadsRes.ok) throw new Error(`HTTP ${workloadsRes.status}`);
      const instData = await instRes.json();
      const nodesData = await nodesRes.json();
      const workloadsData = await workloadsRes.json();
      setInstance(instData.instance);
      setNodes(nodesData.nodes || []);
      setWorkloads(workloadsData.workloads || []);
      // Services is best-effort: listServices already returns only orphans
      // (no matching workload, see the merge decision), and a failure here
      // should not block the rest of the page from rendering.
      if (servicesRes.ok) {
        const servicesData = await servicesRes.json();
        setOrphanServices(servicesData.services || []);
      }
      // Same best-effort treatment: sparklines are a nice-to-have on top
      // of the current values already on `instance`, never worth failing
      // the page load over.
      if (latencyHistoryRes.ok) {
        const latencyHistoryData = await latencyHistoryRes.json();
        setLatencyHistory(latencyHistoryData.series || {});
      }
      if (latencyBaselineRes.ok) {
        setLatencyBaseline(await latencyBaselineRes.json());
      }
      if (alertsRes.ok) {
        const alertsData = await alertsRes.json();
        setAlerts(alertsData.alerts || []);
      }
      // Best-effort, same as latency/services: a Sabbath status fetch
      // failure should never block the rest of the page.
      if (sabbathRes.ok) {
        const sabbathData = await sabbathRes.json();
        setSabbath(sabbathData.sabbath || null);
      }
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAll();

    function startTimer() {
      if (timerRef.current) return;
      timerRef.current = setInterval(() => fetchAll(true), POLL_INTERVAL);
    }
    function stopTimer() {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    function handleVisibility() {
      if (document.hidden) stopTimer();
      else { fetchAll(true); startTimer(); }
    }

    startTimer();
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      stopTimer();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fetchAll]);

  const handleForcePoll = async () => {
    setPolling(true);
    try {
      const res = await fetchWithTenant(`/api/onwatch/instances/${id}/poll`, { method: 'POST' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchAll(true);
    } catch (e) {
      setError(`Force poll failed: ${e.message}`);
    } finally {
      setPolling(false);
    }
  };

  // Reuses the existing generic MD alert endpoints (Task F): they match
  // on {id, tenantId} only, no device-type assumption, so no OnWatch-
  // specific acknowledge/resolve route was needed.
  const handleAckAlert = async (alertId) => {
    try {
      await fetchWithTenant(`/api/md/alerts/${alertId}/acknowledge`, { method: 'POST' });
      await fetchAll(true);
    } catch { /* best-effort refresh follows regardless */ }
  };
  const handleResolveAlert = async (alertId) => {
    try {
      await fetchWithTenant(`/api/md/alerts/${alertId}/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: 'Resolved by operator' }),
      });
      await fetchAll(true);
    } catch { /* best-effort refresh follows regardless */ }
  };

  // UNKNOWN and ERROR both mean we cannot vouch for current state: UNKNOWN
  // is the staleness sweep, ERROR is polling suspended (e.g. AUTH failure).
  // Every downstream "blind" treatment (node cards, workload rows, category
  // dots, exceptions banner) shares this one derivation so none of them
  // drift out of sync with each other.
  const stale = instance?.collectionState === 'UNKNOWN' || instance?.collectionState === 'ERROR';

  // App tier staleness (Phase 2c) is a SEPARATE derivation from the
  // Kubernetes-tier `stale` above: the two tiers fail and recover
  // independently, and averaging them into one status is exactly what
  // Task F says not to do. appConfigured distinguishes "never set up"
  // (nothing to flag) from "was working, has gone stale" or "is actively
  // erroring" (both worth surfacing).
  const appConfigured = !!(instance?.appLastSuccessAt || instance?.appLastError);
  const appStale = appConfigured && (
    !instance.appLastSuccessAt ||
    (Date.now() - new Date(instance.appLastSuccessAt).getTime()) / 1000 > (instance.stalenessSec || 300)
  );

  // Third independent tier (Phase 4a): the host-collection agent. Same
  // "each tier fails and recovers on its own" discipline as k8s/app above -
  // see onwatchHostDisplay.cjs's deriveHostTierStatus for the shared logic.
  const hostTierStatus = useMemo(() => deriveHostTierStatus(instance || {}), [instance]);

  // The "non-healthy only" filter is meaningless once every row reads
  // UNKNOWN. Drop it rather than leave a user stuck on a filter that no
  // longer separates anything.
  useEffect(() => {
    if (stale) setOnlyIssues(false);
  }, [stale]);

  const filteredWorkloads = useMemo(() => {
    let list = workloads;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((w) => w.workloadName.toLowerCase().includes(q) || w.namespace.toLowerCase().includes(q));
    }
    if (onlyIssues) {
      list = list.filter((w) => w.health === 'CRITICAL' || w.health === 'WARNING' || w.health === 'UNKNOWN');
    }
    return list;
  }, [workloads, search, onlyIssues]);

  const grouped = useMemo(() => {
    const byCategory = {};
    for (const cat of CATEGORY_ORDER) byCategory[cat] = [];
    for (const w of filteredWorkloads) {
      const cat = CATEGORY_ORDER.includes(w.category) ? w.category : 'UNCLASSIFIED';
      byCategory[cat].push(w);
    }
    return byCategory;
  }, [filteredWorkloads]);

  const jumpToExceptions = () => {
    setOnlyIssues(true);
    for (const cat of CATEGORY_ORDER) {
      const section = accordionRefs.current[cat];
      if (section && grouped[cat]?.length > 0) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        break;
      }
    }
  };

  if (loading) {
    return <div className="page-content"><div className="md-loading"><Loader2 size={32} className="spin" /><p>Loading cluster health...</p></div></div>;
  }

  if (error && !instance) {
    return (
      <div className="page-content">
        <div className="ow-form-error">{error}</div>
        <Link to="/monitoring/onwatch"><ArrowLeft size={14} /> Back to instances</Link>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="page-header-with-actions">
        <div>
          <Link to="/monitoring/onwatch" style={{ fontSize: 12, color: 'var(--text-tertiary)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <ArrowLeft size={12} /> OnWatch instances
          </Link>
          <h1 className="page-title"><Server size={26} /> {instance.name}</h1>
          {/* 2g-fix, Item 3: diagnostic, not a health indicator - quiet
              styling deliberately, same ow-form-help token every other
              secondary line on this page uses. */}
          {instance.lastPollDurationMs != null && (
            <div className="ow-form-help">
              Last poll {instance.lastPollDurationMs}ms, {instance.lastPollQueryCount ?? '—'} quer{instance.lastPollQueryCount === 1 ? 'y' : 'ies'}
              {instance.lastPollSlowestKey ? `, slowest: ${instance.lastPollSlowestKey} (${instance.lastPollSlowestMs}ms)` : ''}
              <InfoTooltip text="How long the most recent poll cycle took and how many Prometheus queries it ran. Diagnostic only, not a health signal - a slow poll does not mean the appliance is unhealthy." />
            </div>
          )}
        </div>
        <div className="ow-controls">
          <button className="btn btn-secondary btn-sm" onClick={() => fetchAll()}><RefreshCw size={14} /> Refresh</button>
          <button className="btn btn-secondary btn-sm" onClick={handleForcePoll} disabled={polling}>
            {polling ? <Loader2 size={14} className="spin" /> : <Play size={14} />} Force Poll
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowLogWizard(true)}>
            <FileArchive size={14} /> Collect Logs
          </button>
          <Link to={`/monitoring/onwatch/${id}/alert-rules`} className="btn btn-secondary btn-sm"><Bell size={14} /> Alert Rules</Link>
          <Link to="/monitoring/onwatch" className="btn btn-secondary btn-sm"><Settings size={14} /> Settings</Link>
        </div>
      </div>

      {showLogWizard && (
        <LogBundleWizard
          instanceId={id}
          workloads={workloads}
          stale={stale}
          preselectWorkloadName={wizardPreselect}
          onClose={() => { setShowLogWizard(false); setWizardPreselect(null); }}
        />
      )}

      <CollectionStateBanner instance={instance} />
      <AppTierStatusLine instance={instance} appStale={appStale} appConfigured={appConfigured} />
      <HostTierStatusLine instance={instance} hostTierStatus={hostTierStatus} />
      <AlertsPanel
        alerts={alerts}
        k8sStale={stale}
        appStale={appStale}
        appConfigured={appConfigured}
        onAck={handleAckAlert}
        onResolve={handleResolveAlert}
      />
      <ExceptionsBanner workloads={workloads} onJump={jumpToExceptions} stale={stale} lastSuccessAt={instance.lastSuccessAt} />

      {(nodes.length > 0 || appConfigured) && (
        <div className="ow-node-grid">
          {nodes.map((node) => (
            <NodeCard key={node.nodeName} node={node} stale={stale} lastSuccessAt={instance.lastSuccessAt} hostTierStatus={hostTierStatus} />
          ))}
          {nodes.map((node) => (
            <HardwareCard key={`${node.nodeName}-hw`} node={node} hostCollectionLastSuccessAt={instance.hostCollectionLastSuccessAt} />
          ))}
          {appConfigured && <CameraSummaryCard instance={instance} appStale={appStale} instanceId={id} sabbath={sabbath} />}
          {appConfigured && <LicenseCard instance={instance} appStale={appStale} />}
          <PipelineLatencyCard instance={instance} stale={stale} history={latencyHistory} baseline={latencyBaseline} />
          <RecognitionCard instance={instance} stale={stale} />
        </div>
      )}

      <SabbathRecoveryHistoryCard events={sabbath?.recentRecoveryEvents} />

      <div className="ow-toolbar">
        <div className="ow-search-wrap">
          <Search size={14} />
          <input className="form-input" placeholder="Search workloads..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="ow-chip-row">
          <button className={`ow-chip${!onlyIssues ? ' active' : ''}`} onClick={() => setOnlyIssues(false)}>All</button>
          <button
            className={`ow-chip${onlyIssues ? ' active' : ''}`}
            onClick={() => setOnlyIssues(true)}
            disabled={stale}
            title={stale ? 'Health is unknown while collection is stale, so healthy cannot be distinguished from not' : undefined}
          >
            Non-healthy only
          </button>
        </div>
      </div>

      {/* Phase 2g, Task F: one legend for the whole list, not one tooltip
          per row - these badges repeat on every workload, and hundreds of
          identical info icons would be noise rather than help. */}
      <div className="ow-workload-legend">
        <span className="ow-workload-legend-item">
          Ready fraction<InfoTooltip text="Containers ready over containers desired. A service can be running but not ready, which is the failure mode that phase-only monitoring misses." />
        </span>
        <span className="ow-workload-legend-item">
          Restarts 1h<InfoTooltip text="Restarts in the last hour. Lifetime restart totals are meaningless on pods that run for months, so we track rate instead." />
        </span>
        <span className="ow-workload-legend-item">
          Suppressed<InfoTooltip text="Known-noisy component. Reported, never alerted. The GPU telemetry exporter restarts roughly nine times an hour on these appliances with no operational impact." />
        </span>
        <span className="ow-workload-legend-item">
          Consul<InfoTooltip text="The application's own service registry health, separate from Kubernetes container health. A service can be running as a container while failing its application health check." />
        </span>
      </div>

      {CATEGORY_ORDER.map((cat) => {
        const list = grouped[cat];
        if (!list || list.length === 0) return null;
        return (
          <AccordionSection
            key={cat}
            category={cat}
            workloads={list}
            defaultOpen={cat === 'CORE'}
            stale={stale}
            searchActive={!!search.trim() || onlyIssues}
            sectionRef={(el) => { accordionRefs.current[cat] = el; }}
            instanceId={id}
            appStale={appStale}
          />
        );
      })}

      {orphanServices.length > 0 && (
        <div className="ow-accordion-section">
          <div className="ow-accordion-header" style={{ cursor: 'default' }}>
            <HealthDot health={appStale ? 'UNKNOWN' : orphanServices.some((s) => s.health === 'CRITICAL') ? 'CRITICAL' : 'HEALTHY'} />
            <span className="ow-accordion-title">Other services (Consul, no matching workload)</span>
            <span className="ow-accordion-count">{orphanServices.length}</span>
          </div>
          <div className="ow-accordion-body">
            {orphanServices.map((s) => (
              <div key={s.id} className={`ow-workload-row${appStale ? ' ow-stale' : ''}`}>
                <HealthDot health={appStale ? 'UNKNOWN' : s.health} />
                <span className="ow-workload-name">{s.serviceName}</span>
                <div className="ow-workload-meta">
                  {s.tags.slice(0, 3).map((t) => <span key={t} className="ow-kind-badge">{t}</span>)}
                  {s.suppressed && <span className="ow-suppressed-badge" title={s.healthNote || ''}>suppressed</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
